import {
  Button,
  ButtonGroup,
  Grid,
  GridCell,
  Image,
  Input,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectOption,
  Spacer,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  buildLiveAgentPlatformPayload,
  getEnvDotStyle,
  getPlatformLogo,
  indexChannelsByPype,
  mapConnectionToRow,
  normalizeConfigSchema,
  type ConfigField,
  type LiveAgentPlatformFormValues,
} from '../../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../../xstate/app.xstate';
import {
  SOLUTION_GROUPS_TEST_ID_PREFIX,
  SolutionChannelGroups,
  useChannelGroups,
} from '../../solution-channel-groups';
import { GenericModal, GenericModalProps } from '..';
import { useLiveAgentPlatformOptions } from './use-live-agent-platform-options';
import { useModalNavigation } from './use-modal-navigation';

type Env = 'live' | 'sandbox';

/**
 * Why the picker is empty. Worded per-ENVIRONMENT rather than per-project: the
 * catalog is fetched for both envs, and switching Environment here is exactly
 * how a user reaches this state.
 */
const NO_PLATFORMS_IN_ENV_HINT =
  'This environment has no supported live agent platform. Contact Center or a LAAPI integration has to exist on the platform for this environment first.';

export type LiveAgentPlatformModalProps = GenericModalProps & {
  type?: 'add' | 'edit';
  /** Environment the connection belongs to (locked in edit, default in add). */
  env?: Env;
  /**
   * The `PypeConnection` id being edited (undefined for add). Not to be confused
   * with the connection's own `platformId`, which names the platform.
   */
  connectionId?: string;
};

/**
 * The form's shape IS the payload builder's input — one declaration, so a field
 * cannot drift on one side only. `agentRoutingConfig` is narrowed to required
 * here because the form always seeds it (the builder tolerates its absence for
 * callers that don't).
 */
type FormValues = LiveAgentPlatformFormValues & {
  env: Env;
  agentRoutingConfig: Record<string, string>;
};

export const LiveAgentPlatformModal: FC<LiveAgentPlatformModalProps> = ({
  type = 'add',
  env = 'live',
  connectionId,
  modalRef,
  onClose,
}) => {
  const isEdit = type === 'edit';

  const { schemas, connections, pypes } = useProStudioCtxSelector((ctx) => ({
    schemas: ctx.agentRoutingSchemas,
    connections: ctx.pypeConnections,
    pypes: ctx.liveAgentPypes,
  }));
  // Its own selector: only the channel boxes read it.
  const solutions = useProStudioCtxSelector((ctx) => ctx.solutions);
  const customerName = useProStudioCtxSelector((ctx) => ctx.customerId);

  const editingConnection = useMemo(
    () => connections?.find((connection) => connection.id === connectionId),
    [connections, connectionId]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      agentRouting: '',
      env,
      agentRoutingConfig: {},
    },
  });

  /**
   * The connection's stored `settings` minus the platform identity keys — the
   * dropdown owns those, and `integrationName` is re-injected on submit.
   */
  const storedConfig = useMemo<Record<string, unknown>>(() => {
    const raw = editingConnection?.settings ?? {};
    return Object.fromEntries(
      Object.entries(raw).filter(
        ([key]) => key !== 'integrationName' && key !== 'integration_name'
      )
    );
  }, [editingConnection]);

  /**
   * Same values narrowed to what the text inputs can hold. Nested objects (e.g.
   * `integration_headers`) are left out here and merged back on submit, so they
   * survive an edit instead of being dropped.
   */
  const storedFormConfig = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        Object.entries(storedConfig)
          .filter(
            ([, value]) =>
              typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value === 'boolean'
          )
          .map(([key, value]) => [key, String(value)])
      ),
    [storedConfig]
  );

  // Re-seed the form whenever the modal is (re)opened for a different target.
  useEffect(() => {
    reset({
      name: editingConnection?.name ?? '',
      // `platformId` already IS the dropdown's key space (integration name for
      // external, routing value for internal), so it pre-selects directly — no
      // re-resolving from the stored config.
      agentRouting: editingConnection?.platformId ?? '',
      env,
      // Pre-fill the stored credentials so editing doesn't require retyping them
      // (and doesn't overwrite them with blanks on save). `integrationName` is
      // dropped because it identifies the platform — that's the dropdown's job,
      // not a config field, and it is re-injected on submit.
      agentRoutingConfig: storedFormConfig,
    });
  }, [reset, editingConnection, env, type, connectionId, storedFormConfig]);

  const selectedEnv = watch('env');
  const selectedRouting = watch('agentRouting');

  /** The env's FULL catalog — every platform the backend returned. */
  const catalog = useMemo(
    () => schemas?.[selectedEnv] ?? [],
    [schemas, selectedEnv]
  );

  // Which platforms may be picked, what the picker draws, whether the picker is
  // empty, and which schema the selection resolves to — one hook, because the
  // difference between them is load-bearing and was previously pinned by
  // nothing. `offeredOptions` is deliberately not read here: what the user can
  // act on is what the picker RENDERS, and in edit mode that includes the
  // pinned row. See the hook's doc comment.
  const { renderedPlatformOptions, noPlatformsInEnv, selectedSchema } =
    useLiveAgentPlatformOptions({
      catalog,
      isEdit,
      editingPlatformId: editingConnection?.platformId,
      selectedRouting,
    });

  const configFields: ConfigField[] = useMemo(() => {
    const fields = normalizeConfigSchema(selectedSchema?.properties);
    // For external integrations `integrationName` identifies the connector and
    // is injected automatically on submit, so it isn't a user-entered field.
    return selectedSchema?.isExternal
      ? fields.filter((field) => field.key !== 'integrationName')
      : fields;
  }, [selectedSchema]);

  // Channels bound to this connection, named via the env's pypes.
  const channelsByPype = useMemo(
    () => indexChannelsByPype(pypes?.[env] ?? []),
    [pypes, env]
  );
  const channels = useMemo(
    () =>
      editingConnection
        ? mapConnectionToRow(editingConnection, channelsByPype).channels
        : [],
    [editingConnection, channelsByPype]
  );

  // Close the modal BEFORE leaving: it is portalled, so the route change alone
  // would leave it open over the next page. And close it ONLY when there is
  // somewhere to go: a click with no route must not throw the form's unsaved
  // edits away for nothing. See `useModalNavigation`.
  const { goToChannel, goToSolutionVersions } = useModalNavigation(onClose);

  // Same grouping the table cell renders. Derived from the live ctx, so a
  // channel connected while the modal is open lands in its solution's box.
  // While the solutions are unknown it is one flat, unresolved list rather
  // than a false "No solution" box (`useChannelGroups`).
  const channelGroups = useChannelGroups(channels, solutions, customerName);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );
  const toggleGroup = useCallback(
    (key: string) =>
      setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );

  const onSubmit = handleSubmit((values) => {
    // Every routing decision — external vs internal, which config survives, and
    // which callback URL is sent — lives in one pure helper, so each branch is
    // reachable by a test. See `buildLiveAgentPlatformPayload`.
    const base = {
      ...buildLiveAgentPlatformPayload({
        values,
        isEdit,
        editingConnection,
        selectedSchema,
        storedConfig,
      }),
      cb: onClose,
    };

    if (isEdit && connectionId) {
      sendProStudioEvent({
        type: 'updateLiveAgentPlatform',
        payload: { id: connectionId, ...base },
      });
    } else {
      sendProStudioEvent({ type: 'createLiveAgentPlatform', payload: base });
    }
  });

  return (
    <GenericModal
      test-id="live-agent-platform-modal"
      modalRef={modalRef}
      onClose={onClose}
      size="large"
    >
      <TextTitle size="small" textAlign="center">
        {isEdit ? 'Edit' : 'Add'} Live Agent Platform
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        Per-platform credentials and routing. Each connector is scoped to one
        environment.
      </TextBody>
      <Spacer size="xlarge" />

      <Grid>
        {/*
          Rendered whether or not the solutions are known: without them the
          channels are one flat list (no solution boxes, no "No solution"),
          since the `loadingBots` state can end in `#error` with
          `ctx.solutions` still `undefined`.
        */}
        {isEdit && channels.length > 0 && (
          <GridCell xsmall="12">
            <TextBody size="small">Channels Using This Connection:</TextBody>
            <Spacer size="2xsmall" />
            <SolutionChannelGroups
              groups={channelGroups}
              ownerId={connectionId ?? ''}
              expandedKeys={expandedGroups}
              onToggle={toggleGroup}
              onGoToChannel={goToChannel}
              onGoToSolution={goToSolutionVersions}
              testIdPrefix={SOLUTION_GROUPS_TEST_ID_PREFIX.modal}
            />
          </GridCell>
        )}

        <GridCell xsmall="12">
          <TextBody size="small">Connection Name</TextBody>
          <Spacer size="2xsmall" />
          {/*
            `hasError`/`helpText` rather than a sibling paragraph: the DS wires
            the message to the control itself, which is what makes a failed
            submit perceivable instead of silent (WCAG 3.3.1) — clicking Create
            with a required field empty used to do nothing at all, with no
            message, no focus move and no sound.

            The heading stays a `TextBody`: `ps-input` has no `label` property
            (unlike `ps-select`), so there is nothing to move it into. That the
            field therefore has no programmatic NAME is pre-existing DS debt,
            tracked separately rather than worked around here.
          */}
          <Input
            test-id="live-agent-platform-modal-name"
            variant="outlined"
            placeholder="Connection name"
            hasError={!!errors.name}
            helpText={errors.name ? 'Enter a connection name.' : ''}
            {...register('name', { required: true })}
          />
        </GridCell>

        <GridCell xsmall="12">
          <TextBody size="small">Platform</TextBody>
          <Spacer size="2xsmall" />
          <Select
            test-id="live-agent-platform-modal-platform"
            variant="outlined"
            placeholder="Select a platform"
            value={selectedRouting}
            hasError={!!errors.agentRouting}
            helpText={errors.agentRouting ? 'Select a platform.' : ''}
            {...register('agentRouting', { required: true })}
            onChange={(e) => {
              const { value } = e.target as { value: string };
              setValue('agentRouting', value);
              // Reset config when the platform (and thus its fields) changes.
              setValue('agentRoutingConfig', {});
            }}
          >
            {selectedRouting && (
              <Image
                src={getPlatformLogo(selectedRouting)}
                width="24px"
                height="24px"
                slot="prefix"
              />
            )}
            {renderedPlatformOptions.map((option) => (
              <SelectOption
                key={option.agent_routing}
                value={option.agent_routing ?? ''}
              >
                <Image
                  src={getPlatformLogo(option.agent_routing ?? undefined)}
                  width="24px"
                  height="24px"
                  slot="prefix"
                />
                {option.label ?? option.agent_routing}
              </SelectOption>
            ))}
          </Select>
          {/*
            Plain text, deliberately not a tooltip: this has to be readable by
            keyboard and screen-reader users, and a disabled control's tooltip
            reaches neither.

            The live region is ALWAYS mounted and only its CONTENT is
            conditional — a region that appears together with its text is not
            reliably announced (WCAG 4.1.3), and announcing it is the whole
            point here: switching Environment is what empties the picker, and
            the segmented control keeps focus, so nothing else would tell a
            screen-reader user that there is now nothing to pick. Same shape as
            `BlockLoadState` in the channel-settings form, for the same reason.
          */}
          <div role="status">
            {noPlatformsInEnv && (
              <>
                <Spacer size="2xsmall" />
                <TextBody
                  size="small"
                  variant="secondary"
                  test-id="live-agent-platform-modal-no-platforms"
                >
                  {NO_PLATFORMS_IN_ENV_HINT}
                </TextBody>
              </>
            )}
          </div>
        </GridCell>

        <GridCell xsmall="12">
          <TextBody size="small">Environment</TextBody>
          <Spacer size="2xsmall" />
          {/*
            The Sandbox/Live pill toggle every other settings surface uses
            (PSTUDIO-3829, and the same swap the Reporting API credential modal
            made) — it was a plain <Select> here, which read as a third dropdown
            under Connection Name and Platform.

            `env` is driven by `setValue` alone rather than `register`: a
            segmented control is not a form element, so there is nothing for RHF
            to bind to. The value still reaches the submit — it is seeded in
            `defaultValues` and re-seeded by the `reset` above, which is how the
            Reporting API modal's env has always worked.
          */}
          <SegmentedControl
            test-id="live-agent-platform-modal-env"
            // The control sets `role="radiogroup"` on itself, and a group needs
            // a NAME: the "Environment" heading above is a plain `TextBody`, so
            // nothing associates the two and a screen reader announces an
            // unnamed group (WCAG 1.3.1 / 4.1.2). `ps-segmented-control` has no
            // `label` property to move the heading into, so the name is stated
            // here.
            aria-label="Environment"
            value={selectedEnv}
            onChange={(e) => {
              // NARROWED, not asserted: the DS types the event as
              // `CustomEvent<{ value: string }>` and reads that value off the
              // item's DOM attribute, so the union is this form's claim, not
              // the control's. A cast would let a foreign value through to
              // `schemas?.[env]`, which then resolves to `undefined` and empties
              // the picker while the payload still carries the bad env.
              setValue('env', e.detail.value === 'live' ? 'live' : 'sandbox');
              // The catalog is PER-ENV, so a platform picked in the other env may
              // not exist here. Leaving it in the form is not merely stale: the
              // schema then resolves to `undefined` while `platformUnchanged` is
              // false, and the payload goes out with the integration NAME as the
              // internal routing, no callback URL and no legacy config keys —
              // measured.
              setValue('agentRouting', '');
              setValue('agentRoutingConfig', {});
            }}
          >
            {/*
              Locked in edit, as the Select was: a connection belongs to one
              environment for its whole life. Disabling the ITEMS rather than
              the group is what the DS supports — `ps-segmented-control` gates
              selection per item (click and arrow-key alike), and the disabled
              items still render, so the modal keeps showing which environment
              the connection is in.

              `aria-disabled` alongside `disabled`, because they land in
              different places: `disabled` reaches the native <button> inside the
              item's shadow root, while the ARIA ROLE lives on the HOST
              (`role="radio"` + `aria-checked`). Without it a screen reader
              announces a selectable radio the user cannot select — the previous
              `<Select disabled>` set `aria-disabled` itself, so this is parity
              with the control it replaced, not a new nicety.
            */}
            <SegmentedControlItem
              value="sandbox"
              disabled={isEdit}
              aria-disabled={isEdit}
            >
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', selectedEnv === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem
              value="live"
              disabled={isEdit}
              aria-disabled={isEdit}
            >
              <span
                slot="prefix"
                style={getEnvDotStyle('live', selectedEnv === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>
        </GridCell>

        {configFields.map((field) => (
          <GridCell key={field.key} xsmall="12" medium="6">
            <TextBody size="small">{field.label}</TextBody>
            <Spacer size="2xsmall" />
            {field.type === 'select' ? (
              <Select
                test-id={`live-agent-platform-modal-config-${field.key}`}
                variant="outlined"
                value={watch(`agentRoutingConfig.${field.key}`) ?? ''}
                hasError={!!errors.agentRoutingConfig?.[field.key]}
                helpText={
                  errors.agentRoutingConfig?.[field.key]
                    ? `Enter ${field.label}.`
                    : ''
                }
                {...register(`agentRoutingConfig.${field.key}`, {
                  required: field.required,
                })}
                onChange={(e) => {
                  const { value } = e.target as { value: string };
                  setValue(`agentRoutingConfig.${field.key}`, value);
                }}
              >
                {(field.options ?? []).map((option) => (
                  <SelectOption key={option} value={option}>
                    {option}
                  </SelectOption>
                ))}
              </Select>
            ) : (
              <Input
                test-id={`live-agent-platform-modal-config-${field.key}`}
                variant="outlined"
                type={field.type === 'number' ? 'number' : 'text'}
                hasError={!!errors.agentRoutingConfig?.[field.key]}
                helpText={
                  errors.agentRoutingConfig?.[field.key]
                    ? `Enter ${field.label}.`
                    : ''
                }
                {...register(`agentRoutingConfig.${field.key}`, {
                  required: field.required,
                })}
              />
            )}
          </GridCell>
        ))}
      </Grid>

      <ButtonGroup slot="footer">
        <Button
          test-id="live-agent-platform-modal-cancel"
          width="full"
          variant="secondary"
          size="large"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          test-id="live-agent-platform-modal-submit"
          width="full"
          size="large"
          onClick={onSubmit}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </ButtonGroup>
    </GenericModal>
  );
};
